import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import type { Question, QuestionOption, ResponsePayload } from '../../../types';
import {
  hasRenderableMultipleSelectLabel,
  MultipleSelectQuestion,
} from '../../questions/MultipleSelectQuestion';
import { isQuestionResponseValid } from '../../../utils/questionResponseValidation';
import { registerQuestionRenderer, type QuestionRendererComponent } from './registry';

const getOptionText = (option: QuestionOption | undefined) => {
  if (!option) {
    return '';
  }

  if (typeof option.front_text === 'string' && option.front_text.trim().length > 0) {
    return option.front_text.trim();
  }

  if (typeof option.back_text === 'string' && option.back_text.trim().length > 0) {
    return option.back_text.trim();
  }

  if (typeof option.value === 'number') {
    return String(option.value);
  }

  return '';
};

const normalizeSelection = (selection: number[], question: Question) => {
  const validIds = new Set(
    (question.options ?? [])
      .filter(hasRenderableMultipleSelectLabel)
      .map((option) => option?.id)
      .filter((id): id is number => typeof id === 'number'),
  );
  return selection.filter((id) => validIds.has(id));
};

const MultipleSelectRenderer: QuestionRendererComponent = ({
  question,
  existingResponse,
  onDraftChange,
  onHasUserInputChange,
  onValidityChange,
  onExtrasChange,
}) => {
  void onExtrasChange;
  const questionId = question?.id ?? existingResponse?.question ?? -1;
  const prevQuestionIdRef = useRef(questionId);

  const options = useMemo(() => (Array.isArray(question?.options) ? question.options : []), [question]);
  const otherOption = useMemo(
    () => options.find((candidate) => hasRenderableMultipleSelectLabel(candidate) && candidate.is_other),
    [options],
  );

  const [selectedOptionIds, setSelectedOptionIds] = useState<number[]>(() => {
    const selectedFromExisting = existingResponse?.selected_options;
    if (Array.isArray(selectedFromExisting)) {
      return normalizeSelection(selectedFromExisting, question);
    }
    return normalizeSelection([], question);
  });

  const [customOtherValue, setCustomOtherValue] = useState<string>(() => existingResponse?.custom_other_value ?? '');
  const normalizedSelectedOptionIds = useMemo(
    () => normalizeSelection(selectedOptionIds, question),
    [question, selectedOptionIds],
  );

  const emitDraft = useCallback(
    (selection: number[], otherRawValue: string) => {
      const normalizedSelection = normalizeSelection(selection, question);
      const hasSelection = normalizedSelection.length > 0;

      const otherOptionId =
        otherOption && typeof otherOption.id === 'number' ? otherOption.id : null;
      const includesOther = hasSelection && otherOptionId != null
        ? normalizedSelection.includes(otherOptionId)
        : false;
      const trimmedOther = otherRawValue.trim();

      onHasUserInputChange?.(hasSelection);

      if (!hasSelection) {
        onValidityChange?.(false);
        onDraftChange(null);
        return;
      }

      const draft: ResponsePayload = {
        question: questionId,
        selected_options: [...normalizedSelection],
      };

      const selectedTexts = normalizedSelection
        .map((id) => getOptionText(options.find((option) => option.id === id)))
        .filter((text): text is string => Boolean(text));

      if (includesOther) {
        draft.custom_other_value = trimmedOther;
        if (trimmedOther.length > 0) {
          selectedTexts.push(trimmedOther);
        }
      } else if (typeof draft.custom_other_value !== 'undefined') {
        draft.custom_other_value = undefined;
      }

      if (selectedTexts.length > 0) {
        draft.text_value = selectedTexts.join('; ');
      } else {
        draft.text_value = undefined;
      }

      onValidityChange?.(
        isQuestionResponseValid(question, {
          draft,
          selectedOptions: normalizedSelection,
          customOtherValue: trimmedOther,
        }),
      );
      onDraftChange(draft);
    },
    [onDraftChange, onHasUserInputChange, onValidityChange, options, otherOption, question, questionId],
  );

  // Only reset when navigating to a different question
  useEffect(() => {
    if (prevQuestionIdRef.current !== questionId) {
      const selectedFromExisting = existingResponse?.selected_options;
      const initialSelected = Array.isArray(selectedFromExisting)
        ? normalizeSelection(selectedFromExisting, question)
        : normalizeSelection([], question);
      const initialCustomOther = existingResponse?.custom_other_value ?? '';

      setSelectedOptionIds(initialSelected);
      setCustomOtherValue(initialCustomOther);
      emitDraft(initialSelected, initialCustomOther);
      prevQuestionIdRef.current = questionId;
    }
  }, [questionId, existingResponse?.selected_options, existingResponse?.custom_other_value, emitDraft, question]);

  const handleSelectionChange = (nextSelection: number[]) => {
    let resolvedSelection = [...nextSelection];
    let nextCustomOther = customOtherValue;

    if (
      otherOption &&
      typeof otherOption.id === 'number' &&
      !resolvedSelection.includes(otherOption.id)
    ) {
      nextCustomOther = '';
      setCustomOtherValue('');
    }

    setSelectedOptionIds(resolvedSelection);
    emitDraft(resolvedSelection, nextCustomOther);
  };

  const handleCustomOtherChange = (value: string) => {
    setCustomOtherValue(value);
    emitDraft(normalizedSelectedOptionIds, value);
  };

  return (
    <MultipleSelectQuestion
      question={question}
      selectedOptions={normalizedSelectedOptionIds}
      onChange={handleSelectionChange}
      customOtherValue={customOtherValue}
      onCustomOtherChange={handleCustomOtherChange}
    />
  );
};

registerQuestionRenderer(['multiple_select'], MultipleSelectRenderer);
